import { Box } from 'src/design/atoms/Box';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MeetingListDto } from 'src/api/FiretrolApiClient';
import { useMeetings } from 'src/api/queries/meetings/useMeetings';
import {
  useCloneMeeting,
  useCreateMeeting,
} from 'src/api/queries/meetings/useMeetingMutations';
import { useJobs } from 'src/hooks/useJobs';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Header } from 'src/design/organisms/Header';
import { SelectableMeetingTable } from 'src/routes/Jobs/MeetingMinutes/components/MeetingsTable/SelectableMeetingTable';

export const CreateMeetingMinutes: React.FC = () => {
  const { selectedJob } = useJobs();
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = React.useState<string | null>(
    null,
  );
  const [selectedMeeting, setSelectedMeeting] =
    React.useState<MeetingListDto | null>(null);

  const { data: meetings = [], isLoading: loading } = useMeetings(
    selectedJob?.id,
  );
  const cloneMeeting = useCloneMeeting(selectedJob?.id ?? -1);
  const createMeeting = useCreateMeeting(selectedJob?.id ?? -1);

  const createClonedMeeting = () => {
    if (!selectedJob || !selectedMeeting) return;
    cloneMeeting.mutate(selectedMeeting.id, {
      onSuccess: (result) =>
        navigate(`/jobs/${result.jobId}/meetings/edit/${result.id}`),
    });
  };

  const createBlankMeeting = () => {
    if (!selectedJob) return;
    createMeeting.mutate(undefined, {
      onSuccess: (result) =>
        navigate(`/jobs/${result.jobId}/meetings/edit/${result.id}`),
    });
  };

  if (!selectedJob) return null;

  return (
    <>
      <Header
        title="Create Meeting Minutes"
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            {
              text: 'Meeting Minutes',
              href: `/jobs/${selectedJob.id}/meetings`,
            },
            { text: 'Create Meeting' },
          ],
        }}
        helpText="Choose how you want to create a new meeting"
        helpTitle="Create Meeting"
      />

      <Stack>
        <Text c="dimmed">
          Choose one of the following options to create a new meeting minute
        </Text>

        <SimpleGrid cols={2}>
          <Paper>
            <Stack gap="sm">
              <Title order={4} mt={0}>
                Create New Meeting
              </Title>
              <Text size="sm" c="dimmed">
                Create a new meeting from scratch.
              </Text>
              <Group justify="flex-end">
                <CreateButton
                  onClick={createBlankMeeting}
                  loading={createMeeting.isPending}
                >
                  Create New Meeting
                </CreateButton>
              </Group>
            </Stack>
          </Paper>
          <Paper>
            <Stack gap="sm">
              <Title order={4} mt={0}>
                Clone Existing Meeting
              </Title>
              <Text size="sm" c="dimmed">
                Clone an existing meeting's people, topics, and issues.
              </Text>
              <Group justify="flex-end">
                <CreateButton onClick={() => setSelectedOption('clone')}>
                  Clone Existing Meeting
                </CreateButton>
              </Group>
            </Stack>
          </Paper>
        </SimpleGrid>

        <Group justify="flex-end">
          <SecondaryButton
            onClick={() => navigate(`/jobs/${selectedJob.id}/meetings`)}
          >
            Cancel
          </SecondaryButton>
        </Group>

        {selectedOption === 'clone' && (
          <>
            <Divider />
            <Title order={3}>Select meeting to clone</Title>

            <Box>
              <CreateButton
                disabled={!selectedMeeting}
                onClick={createClonedMeeting}
                loading={cloneMeeting.isPending}
              >
                Clone{' '}
                {selectedMeeting?.title && selectedMeeting.title.length > 0
                  ? `"${selectedMeeting.title}"`
                  : 'Meeting'}
              </CreateButton>
            </Box>

            <SelectableMeetingTable
              meetings={meetings}
              selectedMeetingId={selectedMeeting ? selectedMeeting.id : null}
              isLoading={loading}
              onMeetingSelected={(meeting) => setSelectedMeeting(meeting)}
            />
          </>
        )}
      </Stack>
    </>
  );
};
